/**
 * WGUpdatesOverview Component - Working Group Updates Overview and Management - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * Comprehensive working group updates overview with advanced filtering, search capabilities,
 * pagination, and preview modal integration. Features real-time search highlighting,
 * multiple filter criteria (working group, date range, text search), responsive pagination,
 * and sophisticated applied filters management with instant statistics display.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Array} props.updates - Array of working group update objects
 * @param {string|number} props.updates[].id - Unique update identifier
 * @param {string} props.updates[].update - Update content (HTML)
 * @param {string} props.updates[].updateDate - Update date string
 * @param {string} props.updates[].updatedBy - Email of update author
 * @param {string|number} props.updates[].workingGroupNameId - Working group ID reference
 * @returns {JSX.Element} The WGUpdatesOverview component
 * 
 * @example
 * <WGUpdatesOverview updates={workingGroupUpdatesArray} />
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import apiHelper from '../../utils/apiHelper';
import { formatIrishDate, stripHtml, truncateText, cleanAndTruncateHtml } from '../../utils/textUtils';
import { FILE_NAMES } from '../../utils/constants';
import LoadingSpinner from '../Common/LoadingSpinner';
import './WGUpdatesOverview.css';

function WGUpdatesOverview({ updates }) {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('card'); // 'grid' or 'card'
  const [filteredUpdates, setFilteredUpdates] = useState([]);
  const [workingGroupsData, setWorkingGroupsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);
  const [filters, setFilters] = useState({
    workingGroup: 'all',
    dateRange: 'all',
    search: ''
  });
  
  // Loading states
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const [loadingPageNumber, setLoadingPageNumber] = useState(1);

  // Preview modal state
  const [previewModal, setPreviewModal] = useState({
    show: false,
    data: null,
    type: 'workingGroup'
  });

  const getLatestUpdatesMap = () => {
    const latestMap = {};

    filteredUpdates.forEach(update => {
      const groupId = update.workingGroupNameId;
      const existing = latestMap[groupId];

      if (!existing || new Date(update.updateDate) > new Date(existing.updateDate)) {
        latestMap[groupId] = update;
      }
    });

    return latestMap;
  };

  const latestUpdatesMap = getLatestUpdatesMap();


  /**
   * Shows preview modal for working group details
   * 
   * @param {string|number} workingGroupId - ID of the working group to preview
   */
  const showPreviewModal = (workingGroupId) => {
    const workingGroupData = {
      Title: getWorkingGroupTitle(workingGroupId),
      ID: workingGroupId
    };
    setPreviewModal({
      show: true,
      data: workingGroupData,
      type: 'workingGroup'
    });
  };

  /**
   * Closes the preview modal and resets state
   */
  const closePreviewModal = () => {
    setPreviewModal({
      show: false,
      data: null,
      type: 'workingGroup'
    });
  };

  // Fetch working groups data on component mount
  useEffect(() => {
    /**
     * Fetches working groups structure data from API
     * Sets loading state and handles errors gracefully
     */
    const fetchWorkingGroups = async () => {
      setIsLoading(true);
      try {
        const wgDataResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.WG_STRUCTURE);
        setWorkingGroupsData(wgDataResult.fileContent || []);
      } catch (err) {
        console.error('Error fetching working groups:', err);
      }
    };

    fetchWorkingGroups();
  }, []);

  // Filter updates when data or filters change
  useEffect(() => {
    if (workingGroupsData.length > 0 && updates) {
      filterUpdates();
      setIsLoading(false);
    }
  }, [updates, workingGroupsData, filters]);

  /**
   * Gets working groups that have actual updates available
   * Filters out groups without content and steering groups
   * 
   * @returns {Array} Array of working groups with valid updates
   */
  const getWorkingGroupsWithUpdates = () => {
    if (!updates || !workingGroupsData.length) return [];
    
    // Get unique working group IDs from updates that have valid content
    const updatesWithContent = updates.filter(update => {
      if (!update.update || update.update.trim() === '') return false;
      
      // Find the working group and check its type
      const workingGroup = workingGroupsData.find(group => group.id === update.workingGroupNameId);
      return workingGroup && workingGroup.workingGroupType !== "Sterring Group";
    });
    
    const uniqueWorkingGroupIds = [...new Set(updatesWithContent.map(update => update.workingGroupNameId))];
    
    // Filter workingGroupsData to only include groups that have updates
    return workingGroupsData.filter(group => 
      uniqueWorkingGroupIds.includes(group.id) && 
      group.workingGroupType !== "Sterring Group"
    );
  };

  /**
   * Filters and sorts updates based on current filter criteria
   * Applies working group, date range, and search filters
   * 
   * Note: Uses stripHtml() for search filtering to properly handle HTML content
   * in update.update field which may contain complex HTML with inline styles
   */
  const filterUpdates = () => {
    if (!updates) return;
    
    // First filter out any updates without content and exclude Sterring Group type
    let filtered = [...updates].filter(update => {
      // Check for valid content
      if (!update.update || update.update.trim() === '') return false;
      
      // Find the working group and check its type
      const workingGroup = workingGroupsData.find(group => group.id === update.workingGroupNameId);
      return workingGroup && workingGroup.workingGroupType !== "Sterring Group";
    });

    // Then apply the rest of the filters
    if (filters.workingGroup !== 'all') {
      filtered = filtered.filter(update => 
        update.workingGroupNameId === parseInt(filters.workingGroup)
      );
    }

    if (filters.dateRange !== 'all') {
      const now = new Date();
      const cutoffDate = new Date();
      switch (filters.dateRange) {
        case 'month':
          cutoffDate.setMonth(now.getMonth() - 1);
          break;
        case 'quarter':
          cutoffDate.setMonth(now.getMonth() - 3);
          break;
        case 'year':
          cutoffDate.setFullYear(now.getFullYear() - 1);
          break;
        default:
          break;
      }
      filtered = filtered.filter(update => 
        new Date(update.updateDate) >= cutoffDate
      );
    }

    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(update => {
        // Clean the HTML first, then strip it for searching to handle complex HTML with inline styles
        const cleanedHtml = cleanAndTruncateHtml(update.update, 1000); // Use larger limit for search
        const searchableText = stripHtml(cleanedHtml).toLowerCase();
        return searchableText.includes(searchTerm) ||
               getWorkingGroupTitle(update.workingGroupNameId).toLowerCase().includes(searchTerm);
      });
    }

    // Sort by date (newest first)
    filtered.sort((a, b) => new Date(b.updateDate) - new Date(a.updateDate));

    setFilteredUpdates(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  /**
   * Gets the title of a working group by its ID
   * Recursively searches through nested group structures
   * 
   * @param {string|number} id - Working group ID to find
   * @returns {string} Working group title or 'Unknown Working Group'
   */
  const getWorkingGroupTitle = (id) => {
    const findGroupTitle = (groups) => {
      for (let group of groups || []) {
        if (group.id === id) {
          return group.workingGroupName;
        }
        if (group.wgSubGroups) {
          const title = findGroupTitle(group.wgSubGroups);
          if (title) return title;
        }
      }
      return null;
    };

    return findGroupTitle(workingGroupsData) || 'Unknown Working Group';
  };

  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredUpdates.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredUpdates.length / itemsPerPage);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  /**
   * Gets currently applied filters for display
   * 
   * @returns {Array} Array of filter objects with removal handlers
   */
  const getAppliedFilters = () => {
    const appliedFilters = [];
    
    if (filters.search) {
      appliedFilters.push({
        type: 'search',
        label: 'Search',
        value: filters.search,
        onRemove: () => {
          setFilters(prev => ({ ...prev, search: '' }));
        }
      });
    }
    
    if (filters.workingGroup !== 'all') {
      const groupName = getWorkingGroupTitle(parseInt(filters.workingGroup));
      appliedFilters.push({
        type: 'workingGroup',
        label: 'Working Group',
        value: groupName,
        onRemove: () => {
          setFilters(prev => ({ ...prev, workingGroup: 'all' }));
        }
      });
    }
    
    if (filters.dateRange !== 'all') {
      const dateRangeLabels = {
        'month': 'Last Month',
        'quarter': 'Last Quarter', 
        'year': 'Last Year'
      };
      appliedFilters.push({
        type: 'dateRange',
        label: 'Date Range',
        value: dateRangeLabels[filters.dateRange],
        onRemove: () => {
          setFilters(prev => ({ ...prev, dateRange: 'all' }));
        }
      });
    }
    
    return appliedFilters;
  };

  /**
   * Resets all filters to their default state
   */
  const resetFilters = () => {
    setFilters({
      workingGroup: 'all',
      dateRange: 'all',
      search: ''
    });
  };

  /**
   * Checks if any filters are currently active
   * 
   * @returns {boolean} True if any filters are applied
   */
  const isFilterActive = () => {
    return filters.workingGroup !== 'all' || 
           filters.dateRange !== 'all' || 
           filters.search !== '';
  };

  /**
   * Handles page change with loading states and smooth scrolling
   * 
   * @param {number} newPage - Page number to navigate to
   */
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      setLoadingPageNumber(newPage);
      setCurrentPage(newPage);
      setIsLoadingPage(true);
      
      // Scroll to top
      document.querySelector('.updates-overview')?.scrollIntoView({ behavior: 'smooth' });
      
      // Simulate loading delay
      setTimeout(() => {
        setIsLoadingPage(false);
      }, 300);
    }
  };

  /**
   * Highlights search terms in text content
   * 
   * @param {string} text - Text to highlight
   * @param {string} searchTerm - Term to highlight
   * @returns {string} HTML string with highlighted terms
   */
  const highlightSearchTerm = (text, searchTerm) => {
    // Add type checking to ensure text is a string
    if (typeof text !== 'string') {
      return text;
    }
    
    if (!searchTerm.trim()) return text;
    
    const regex = new RegExp(`(${searchTerm.trim()})`, 'gi');
    return text.replace(regex, `<span class="highlight-match">$1</span>`);
  };

  if (isLoading) {
    return (
      <div className="updates-overview">
        <div className="loading-container">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="updates-overview">
      {(!workingGroupsData.length || !updates?.length) ? (
        <div className="loading-container">
          <LoadingSpinner />
        </div>
      ) : (
        <>
          {/* Preview Modal - Same as WGVisual */}
          {previewModal.show && (
            <div className="preview-modal-overlay" onClick={closePreviewModal}>
              <div className="preview-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="preview-modal-header">
                  <h3>{previewModal.data?.Title}</h3>
                  <button className="preview-modal-close" onClick={closePreviewModal}>
                    <i className="fas fa-times"></i>
                  </button>
                </div>
                <div className="preview-modal-body">
                  <div className="preview-badge">
                    <i className="fas fa-flask"></i>
                    Preview Mode
                  </div>
                  <p>
                    Working Group details including Terms of Reference, Meeting Minutes, Team Members, and Reference Documents will be available in September 2025.
                  </p>
                  <div className="preview-features">
                    <h4>Coming Soon:</h4>
                    <ul>
                      <li><i className="fas fa-file-alt"></i> Terms of Reference</li>
                      <li><i className="fas fa-users"></i> Meet the Team</li>
                      <li><i className="fas fa-calendar"></i> Meeting Minutes</li>
                      <li><i className="fas fa-folder"></i> Reference Documents</li>
                    </ul>
                  </div>
                </div>
                <div className="preview-modal-footer">
                  <button className="btn btn-secondary" onClick={closePreviewModal}>
                    Close Preview
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Filters Section - ResearchCentre Style */}
          <div className="wg-filters-section">
            <div className="wg-filters-bar">
              <div className="wg-search-and-controls-row">
                <div className="wg-search-box">
                  <i className="fas fa-search wg-search-magnifier"></i>
                  <input
                    type="text"
                    placeholder="Search updates and working groups..."
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  />
                  {filters.search && (
                    <i 
                      className="fas fa-times wg-clear-search" 
                      onClick={() => setFilters(prev => ({ ...prev, search: '' }))}
                    />
                  )}
                </div>

                <div className="wg-inline-filter-controls">
                  <select 
                    value={filters.workingGroup}
                    onChange={(e) => setFilters(prev => ({ ...prev, workingGroup: e.target.value }))}
                  >
                    <option value="all">All Working Groups (with updates)</option>
                    {/* Only show working groups that actually have updates */}
                    {getWorkingGroupsWithUpdates()
                      .map(group => (
                        <option key={group.id} value={group.id}>
                          {group.workingGroupName}
                        </option>
                    ))}
                  </select>

                  <select 
                    value={filters.dateRange}
                    onChange={(e) => setFilters(prev => ({ ...prev, dateRange: e.target.value }))}
                  >
                    <option value="all">All Time</option>
                    <option value="month">Last Month</option>
                    <option value="quarter">Last Quarter</option>
                    <option value="year">Last Year</option>
                  </select>

                  <div className="wg-view-toggle">
           
                    <button 
                      className={viewMode === 'card' ? 'active' : ''}
                      onClick={() => setViewMode('card')}
                      title="Card View"
                    >
                      <i className="fas fa-th-list"></i>
                    </button>
                    <button 
                      className={viewMode === 'grid' ? 'active' : ''}
                      onClick={() => setViewMode('grid')}
                      title="Grid View"
                    >
                      <i className="fas fa-th-large"></i>
                    </button>
                  </div>

                  {isFilterActive() && (
                    <i 
                      className="fa-solid fa-rotate-left resetIcon" 
                      title='Reset Filters' 
                      onClick={resetFilters}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Applied Filters & Stats Panel - ResearchCentre Style */}
          <div className="wg-filters-status">
            <div className="applied-filters-section">
              {getAppliedFilters().length > 0 && (
                <div className="applied-filters">
                  <span className="applied-filters-label">Applied filters:</span>
                  <div className="filter-tags">
                    {getAppliedFilters().map((filter, index) => (
                      <div key={`${filter.type}-${index}`} className="filter-tag">
                        <span className="filter-tag-label">{filter.label}:</span>
                        <span className="filter-tag-value">{filter.value}</span>
                        <button 
                          className="filter-tag-remove"
                          onClick={filter.onRemove}
                          title={`Remove ${filter.label} filter`}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="wg-instant-stats-panel">
              <div className="stats-info">
                <div className="stats-description">
                  <i className="fa-solid fa-chart-simple"></i>
                  {filteredUpdates.length === 1 ? '1 update' : `${filteredUpdates.length} updates`} found
                </div>
              </div>
            </div>
          </div>

          {/* Loading State */}
          {isLoadingPage && (
            <div className="page-loading">
              <i className="fas fa-spinner fa-spin"></i>
              <span>Loading page {loadingPageNumber}...</span>
            </div>
          )}

          {/* Updates Container */}
          <div className={`wg-updates-container ${viewMode}`}>
            {currentItems.length > 0 ? (
              currentItems.map(update => (
                <div key={update.id} className="wg-update-card">
                  <div className="wg-update-header">
                    <div className="wg-update-title-section">
                      <Tippy content={getWorkingGroupTitle(update.workingGroupNameId)}>
                        <h4 className="wg-update-title">
                          {filters.search.trim() ? (
                            <span dangerouslySetInnerHTML={{ 
                              __html: highlightSearchTerm(
                                truncateText(getWorkingGroupTitle(update.workingGroupNameId), 45), 
                                filters.search
                              )
                            }} />
                          ) : (
                            truncateText(getWorkingGroupTitle(update.workingGroupNameId), 45)
                          )}
                        </h4>
                      </Tippy>
                      <div className="wg-update-category-badge">
                        <i className="fas fa-users"></i>
                        <span>Working Group</span>
                      </div>
                    </div>
                    <div className="wg-update-date">
                      <i className="fas fa-calendar-alt"></i>
                      <span>{formatIrishDate(update.updateDate)}</span>
                    </div>
                  </div>
                  
                  <div className="wg-update-content">
                    <div className="wg-update-text">
                      {filters.search.trim() ? (
                        <div dangerouslySetInnerHTML={{ 
                          __html: highlightSearchTerm(
                            // Use cleanAndTruncateHtml to clean messy HTML while preserving formatting like lists, bold text
                            cleanAndTruncateHtml(update.update, 2500), 
                            filters.search
                          )
                        }} />
                      ) : (
                        <div dangerouslySetInnerHTML={{ 
                          __html: cleanAndTruncateHtml(update.update, 2500)
                        }} />
                      )}
                    </div>
                  </div>
                  
                  <div className="wg-update-footer">
                    <div className="wg-update-tags">
                      {latestUpdatesMap[update.workingGroupNameId]?.id === update.id && (
                        <span className="wg-update-tag wg-tag-primary">
                          <i className="fas fa-clock"></i>
                          Latest Update
                        </span>
                      )}
                    </div>
                    <button
                      className="wg-view-more-btn"
                      onClick={() => navigate(`/WorkingGroups/WorkingGroup/${update.workingGroupNameId}`)}
                    >
                      <span>Go to Group </span>
                      <i className="fas fa-arrow-right"></i>
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="wg-no-updates-message">
                <div className="wg-no-updates-content">
                  <i className="fa-regular fa-folder-open"></i>
                  <h3>No updates found</h3>
                  <p>
                    {isFilterActive() 
                      ? 'Try adjusting your search criteria or filters to see more results.'
                      : 'No working group updates are currently available.'
                    }
                  </p>
                  {isFilterActive() && (
                    <button className="btn btn-secondary" onClick={resetFilters}>
                      Clear Filters
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Professional Pagination */}
          {filteredUpdates.length > itemsPerPage && !isLoadingPage && (
            <div className="wg-pagination">
              <div className="wg-pagination-info">
                Page {currentPage} of {totalPages}
              </div>
              <div className="wg-pagination-controls">
                <button 
                  className="wg-pagination-btn"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  title="First Page"
                >
                  <i className="fas fa-angle-double-left"></i>
                </button>
                <button 
                  className="wg-pagination-btn"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  title="Previous Page"
                >
                  <i className="fas fa-angle-left"></i>
                </button>
                
                {/* Page numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      className={`wg-pagination-btn ${pageNum === currentPage ? 'active' : ''}`}
                      onClick={() => handlePageChange(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                
                <button 
                  className="wg-pagination-btn"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  title="Next Page"
                >
                  <i className="fas fa-angle-right"></i>
                </button>
                <button 
                  className="wg-pagination-btn"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  title="Last Page"
                >
                  <i className="fas fa-angle-double-right"></i>
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default WGUpdatesOverview; 